import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import { FileDown, QrCode } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui-kit";
import { savePdf } from "@/components/ReceiptPdf";
import { usePrefs } from "@/contexts/PrefsContext";
import { evTitle, fmtMoney } from "@/components/EventCard";
import { fmtDateTime } from "@/lib/time";
import { imgSrc } from "@/lib/img";
import type { AppEvent, Venue } from "@/types";

type Format = "poster" | "cards";
const RED = "#FB0330";
const INK = "#1C1612";
const venueUrl = (id: string) => `${window.location.origin}/venues/${id}`;
const eventUrl = (id: string) => `${window.location.origin}/events/${id}?book=1`;

const QR = ({ url, size }: { url: string; size: number }) => (
  <QRCodeCanvas value={url} size={size} level="H" marginSize={2} fgColor={INK}
    imageSettings={{ src: "/logo.png", height: size * 0.2, width: size * 0.2, excavate: true }} />
);

// One printable face; scale 1 = A4 poster (794px wide), 0.5 = A6 card
const Face = ({ v, scale }: { v: Venue; scale: number }) => {
  const { t } = usePrefs();
  const px = (n: number) => Math.floor(n * scale);
  const name = v.name_ar.length > 24 ? 42 : v.name_ar.length > 14 ? 50 : 62;
  const url = venueUrl(v.venue_id);
  return (
    <div dir="rtl" style={{ width: px(794), height: px(1122), background: "#fff", color: INK, display: "flex", flexDirection: "column", alignItems: "center", boxSizing: "border-box", padding: `${px(56)}px ${px(48)}px`, fontFamily: "Cairo, sans-serif", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: px(22), background: RED }} />
      <div style={{ display: "flex", alignItems: "center", gap: px(14), marginTop: px(24) }}>
        <img src="/logo.png" alt="" style={{ width: px(76), height: px(76) }} />
        <span style={{ fontSize: px(44), fontWeight: 900 }}>وين نروح</span>
      </div>
      <div style={{ marginTop: px(40), fontSize: px(name), fontWeight: 900, textAlign: "center", lineHeight: 1.25 }}>{v.name_ar}</div>
      {v.name_en && <div dir="ltr" style={{ marginTop: px(6), fontSize: px(28), fontWeight: 700, color: "#6b625b" }}>{v.name_en}</div>}
      <div style={{ marginTop: px(36), padding: px(20), borderRadius: px(40), border: `${px(6)}px solid ${RED}` }}>
        <QR url={url} size={px(400)} />
      </div>
      <div style={{ marginTop: px(32), fontSize: px(40), fontWeight: 900, color: RED, textAlign: "center" }}>{t("qr.scan")}</div>
      <div style={{ marginTop: px(10), fontSize: px(26), fontWeight: 600, color: "#6b625b", textAlign: "center" }}>{t("qr.scanSub")}</div>
      <div dir="ltr" style={{ marginTop: "auto", paddingTop: px(16), fontSize: px(18), color: "#8a817a" }}>{url.replace(/^https?:\/\//, "")}</div>
    </div>
  );
};

const Sheet = ({ v, format }: { v: Venue; format: Format }) =>
  format === "poster" ? <Face v={v} scale={1} /> : (
    <div style={{ width: 794, height: 1122, display: "grid", gridTemplateColumns: "397px 397px", gridTemplateRows: "561px 561px", background: "#fff", position: "relative" }}>
      {[0, 1, 2, 3].map((i) => <Face key={i} v={v} scale={0.5} />)}
      <div style={{ position: "absolute", top: 0, bottom: 0, left: 396, borderLeft: "2px dashed #b9b1a9" }} />
      <div style={{ position: "absolute", left: 0, right: 0, top: 560, borderTop: "2px dashed #b9b1a9" }} />
    </div>
  );

export const QrPosterButton = ({ v }: { v: Venue }) => {
  const { t } = usePrefs();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Format | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const download = async (format: Format) => {
    setBusy(format);
    try {
      await new Promise((r) => setTimeout(r, 300));
      await savePdf(ref.current!.firstElementChild!, `wayn-nrouh-qr-${format}-${v.venue_id}.pdf`, 0);
      toast.success(t("qr.downloaded"));
    } catch {
      toast.error(t("qr.failed"));
    } finally {
      setBusy(null);
    }
  };
  const opt = (f: Format, title: string, sub: string) => (
    <button key={f} disabled={!!busy} onClick={() => download(f)} data-testid={`qr-download-${f}-${v.venue_id}`}
      className="w-full rounded-2xl border p-4 text-start flex items-center gap-3 hover:border-primary hover:bg-primary/5 transition-[background-color,border-color] disabled:opacity-50">
      <FileDown className="h-5 w-5 text-primary shrink-0" />
      <span className="flex-1"><b className="block">{title}</b><span className="text-xs text-muted-foreground">{sub}</span></span>
      {busy === f && <span className="text-xs">{t("common.loading")}</span>}
    </button>
  );
  return (
    <>
      <span title={v.published ? undefined : t("qr.unpublished")} data-testid={`qr-poster-wrap-${v.venue_id}`} className="inline-flex">
        <button onClick={() => setOpen(true)} disabled={!v.published} data-testid={`qr-poster-${v.venue_id}`}
          className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full bg-accent text-xs font-bold hover:bg-primary hover:text-primary-foreground transition-[background-color,color] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-accent disabled:hover:text-foreground">
          <QrCode className="h-3.5 w-3.5" />{t("qr.button")}
        </button>
      </span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="qr-poster-dialog" className="max-w-md rounded-3xl">
          <DialogHeader className="text-start pe-8"><DialogTitle>{t("qr.title")}</DialogTitle></DialogHeader>
          <div className="flex justify-center rounded-2xl bg-white p-4 border" data-testid="qr-poster-preview"><QR url={venueUrl(v.venue_id)} size={170} /></div>
          <p className="text-sm text-muted-foreground text-center">{t("qr.hint")}</p>
          <div className="space-y-2">
            {opt("poster", t("qr.poster"), t("qr.posterSub"))}
            {opt("cards", t("qr.cards"), t("qr.cardsSub"))}
          </div>
        </DialogContent>
      </Dialog>
      {busy && createPortal(<div ref={ref} style={{ position: "fixed", left: -10000, top: 0 }}><Sheet v={v} format={busy} /></div>, document.body)}
    </>
  );
};

// A4 poster for a single event (texts are Arabic: posters are for customers in Iraq)
const EventFace = ({ e }: { e: AppEvent }) => {
  const url = eventUrl(e.event_id);
  const full = evTitle(e, "ar");
  const title = full.length > 90 ? `${full.slice(0, 88)}…` : full;
  const when = fmtDateTime(e.start_at, "ar", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
  const price = e.is_free ? "مجاناً" : `${(e.options?.length || 0) > 1 ? "يبدأ من " : ""}${e.price_from.map(fmtMoney).join(" / ")}`;
  const size = title.length > 50 ? 32 : title.length > 30 ? 42 : title.length > 18 ? 50 : 58;
  const imgH = title.length > 50 ? 300 : title.length > 18 ? 340 : 390;
  return (
    <div dir="rtl" style={{ width: 794, height: 1122, background: "#fff", color: INK, display: "flex", flexDirection: "column", boxSizing: "border-box", padding: "40px 48px 28px", fontFamily: "Cairo, sans-serif", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 22, background: RED }} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
        <img src="/logo.png" alt="" style={{ width: 60, height: 60 }} />
        <span style={{ fontSize: 34, fontWeight: 900 }}>وين نروح</span>
        <span style={{ marginInlineStart: "auto", fontSize: 22, fontWeight: 700, color: "#6b625b" }}>{e.venue?.name_ar}</span>
      </div>
      {e.poster && <img src={imgSrc(e.poster)} crossOrigin="anonymous" alt="" style={{ marginTop: 22, width: "100%", height: imgH, objectFit: "cover", borderRadius: 32 }} />}
      <div style={{ marginTop: 26, paddingBottom: 6, fontSize: size, fontWeight: 900, lineHeight: 1.35 }}>{title}</div>
      <div style={{ marginTop: 12, fontSize: 28, fontWeight: 800 }}>{when}</div>
      <div style={{ marginTop: 8, display: "flex", gap: 16, alignItems: "center", fontSize: 26, fontWeight: 800 }}>
        <span style={{ background: RED, color: "#fff", borderRadius: 999, padding: "0 22px 8px", height: 54, lineHeight: "46px", display: "inline-block", boxSizing: "border-box" }}><bdi>{price}</bdi></span>
        {e.area && <span style={{ color: "#6b625b", fontWeight: 700 }}>{e.area}</span>}
      </div>
      <div style={{ marginTop: "auto", paddingTop: 20, display: "flex", alignItems: "center", gap: 28 }}>
        <div style={{ padding: 14, borderRadius: 28, border: `5px solid ${RED}`, lineHeight: 0 }}><QR url={url} size={250} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 44, fontWeight: 900, color: RED, lineHeight: 1.2 }}>امسح الكود واحجز</div>
          <div style={{ marginTop: 10, fontSize: 24, fontWeight: 600, color: "#6b625b" }}>يفتح صفحة الحجز مباشرة على موبايلك</div>
          <div dir="ltr" style={{ marginTop: 14, fontSize: 14, color: "#8a817a", textAlign: "right", wordBreak: "break-all", lineHeight: 1.4 }}>{url.replace(/^https?:\/\//, "").replace("?book=1", "")}</div>
        </div>
      </div>
    </div>
  );
};

export const EventQrPosterButton = ({ e }: { e: AppEvent }) => {
  const { t } = usePrefs();
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const download = async () => {
    setBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 300));
      await Promise.all([...ref.current!.querySelectorAll("img")].map((im) => (im.complete ? null : new Promise((r) => { im.onload = im.onerror = r; }))));
      await savePdf(ref.current!.firstElementChild!, `wayn-nrouh-qr-event-${e.event_id}.pdf`, 0);
      toast.success(t("qr.downloaded"));
    } catch {
      toast.error(t("qr.failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button onClick={download} disabled={busy} data-testid={`event-qr-poster-${e.event_id}`} className="h-8 px-3 rounded-full bg-accent text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50">
        <QrCode className="h-3 w-3" />{busy ? t("common.loading") : t("qr.eventButton")}
      </button>
      {busy && createPortal(<div ref={ref} style={{ position: "fixed", left: -10000, top: 0 }}><EventFace e={e} /></div>, document.body)}
    </>
  );
};
